import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { VideoCredits } from '../../../../redux/api/fymAPI.types';
import { planNameMessages } from '../../config/music24PlanNames';
import { CreditBlockView } from './creditBlockView';
import { creditMessages } from './creditMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the credit block; each part uses its own slice of it */
import styles from './StudioCreditBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreditSplitProps {
  credits: VideoCredits;
  view: CreditBlockView;
}

/**
 * The split under "How?" (design release-kit t5): a two-colour meter (plan and packs), the plan
 * with its chip and "N of M left", the packs that never expire, the reset day of the plan
 * credits and the rule "Plan first, then packs".
 */
const CreditSplitComponent: React.FC<CreditSplitProps> = ({ credits, view }) => {
  const intl = useIntl();
  const { limit, packBalance, resetsAt, plan } = credits;

  const lines = useMemo(() => {
    const list: Array<{ key: string; label: React.ReactNode; value: string }> = [];
    if (limit !== null && view.planLeft !== null) {
      list.push({
        key: 'plan',
        label: (
          <>
            {intl.formatMessage(creditMessages.blockSplitPlan)}
            <span className={styles.planChip}>{intl.formatMessage(planNameMessages[plan])}</span>
          </>
        ),
        value: intl.formatMessage(creditMessages.blockSplitPlanLeft, { left: view.planLeft, limit }),
      });
    }
    list.push({
      key: 'packs',
      label: intl.formatMessage(creditMessages.blockSplitPacks),
      value:
        packBalance > 0
          ? intl.formatMessage(creditMessages.blockSplitPacksValue, { count: packBalance })
          : intl.formatMessage(creditMessages.blockSplitPacksNone),
    });
    list.push({
      key: 'resets',
      label: intl.formatMessage(creditMessages.blockSplitResets),
      value: intl.formatMessage(creditMessages.blockSplitResetsValue, {
        date: intl.formatDate(resetsAt, { day: 'numeric', month: 'short' }),
      }),
    });
    list.push({
      key: 'spends',
      label: intl.formatMessage(creditMessages.blockSplitSpends),
      value: intl.formatMessage(creditMessages.blockSplitSpendsValue),
    });
    return list;
  }, [intl, limit, packBalance, plan, resetsAt, view.planLeft]);

  return (
    <div className={styles.split} data-testid="credit-block-split">
      <div className={styles.meter} aria-hidden="true">
        {/* The widths come from the data, so they cannot live in the stylesheet. */}
        <i className={styles.meterPlan} style={{ width: `${view.planPercent}%` }} />
        <i className={styles.meterPack} style={{ width: `${view.packPercent}%` }} />
      </div>
      {lines.map((line) => (
        <div key={line.key} className={styles.line}>
          <span className={styles.lineLabel}>{line.label}</span>
          <b>{line.value}</b>
        </div>
      ))}
    </div>
  );
};

export const CreditSplit = TypedMemo(CreditSplitComponent);
